import * as React from 'react';
import { Theme, StyleRules } from '@material-ui/core/styles';
import withStyles, { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Paper,
  Select,
  MenuItem,
} from '@material-ui/core';
import AlertDialog from './AlertDialog';
import {
  statusPatterns,
  SearchConditions,
  SearchResult as SearchType,
} from '../modules/search';
import { TagsResult as TagsType } from '../modules/tags';

interface Props {
  classes: ClassNameMap;
  search: SearchType;
  conditions: SearchConditions;
  onChangePage: (conditions: SearchConditions) => void;
  tags: TagsType | null;
  newCategories: number[];
  onChangeNewTagId: (index: number, category: number | null) => void;
  onUpdate: (index: number, id: string, newTagId: number, status: number) => Promise<void>;
  updateMessages: string[];
  onSearch: () => void;
  onDelete: (index: number, id: string) => Promise<void>;
}

interface State {
  modalIsOpen: boolean;
  targetSentence: string | null;
  confirmAction: string;
  confirmBtnMessage: string;
  confirmMessage: string;
  index: string | null;
  id: string | null;
  status: string | null;
}

const style = (theme: Theme): StyleRules => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing.unit * 2,
  },
  actionButton: {
    margin: theme.spacing.unit,
  },
});

class SearchList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalIsOpen: false,
      targetSentence: '',
      confirmAction: '',
      confirmBtnMessage: '',
      confirmMessage: '',
      index: null,
      id: null,
      status: null,
    };

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  // 確定/確定取消
  handleUpdate = (e: React.MouseEvent<HTMLButtonElement>) => {
    const index = e.currentTarget.getAttribute('data-index');
    const id = e.currentTarget.getAttribute('data-id');
    const status = e.currentTarget.getAttribute('data-new_status');

    this.setState({
      index,
      id,
      status,
      confirmAction: 'submit',
      confirmBtnMessage: '実行',
      confirmMessage: '実行してよろしいですか？',
    });
  }

  // 削除
  handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    const index = e.currentTarget.getAttribute('data-index');
    const id = e.currentTarget.getAttribute('data-id');

    this.setState({
      index,
      id,
      confirmAction: 'delete',
      confirmBtnMessage: '削除',
      confirmMessage: '削除してよろしいですか？',
    });
  }

  // 各Action用確認ダイアログ
  closeConfirm = () => {
    this.setState({
      confirmBtnMessage: '',
      confirmMessage: '',
    });
  }

  // 各Action実行
  onSubmit = () => {
    const { confirmAction, index, id, status } = this.state;
    const { newCategories, onUpdate, onDelete, onSearch } = this.props;

    switch (confirmAction) {
      case 'delete':
        if (index && id) {
          onDelete(+index, id).then(() => {
            onSearch();
          });
        }
        break;
      case 'submit':
        if (index && id && status) {
          onUpdate(+index, id, newCategories[index], +status).then(() => {
            onSearch();
          });
        }
        break;
    }
    this.closeConfirm();
  }

  handleChangePage = (e: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
    const { conditions, onChangePage } = this.props;
    conditions.page = newPage;
    conditions.limit = conditions.limit;
    conditions.offset = newPage * conditions.limit;
    onChangePage(conditions);
  }

  handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement >) => {
    const { conditions, onChangePage } = this.props;
    conditions.limit = +e.target.value;
    onChangePage(conditions);
  }

  // 検索結果1件毎に新カテゴリが変更されたら呼ばれる
  handleChangeNewTagId = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { onChangeNewTagId } = this.props;
    const { value } = e.target;
    const key = e.currentTarget.getAttribute('data-category_key');
    if (key && value) {
      onChangeNewTagId(+key, +value);
    }
  }

  // 文書表示ダイアログ
  openModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    const sentence = e.currentTarget.getAttribute('data-sentence');
    this.setState({ modalIsOpen: true, targetSentence: sentence });
  }
  closeModal() {
    this.setState({ modalIsOpen: false });
  }

  render() {
    const {
      classes,
      search,
      conditions,
      tags,
      newCategories,
    } = this.props;

    const {
      confirmBtnMessage,
      confirmMessage,
    } = this.state;

    return (
      <React.Fragment>
        <AlertDialog
          btnMessage={confirmBtnMessage}
          message={confirmMessage}
          messageDetail={''}
          onClose={this.closeConfirm}
          onSubmit={this.onSubmit}
        />
        <Paper className={classes.root}>
          <div className={classes.tableWrapper}>
            <Table className={classes.table} aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  <TableCell>申請者名</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>ファイル名</TableCell>
                  <TableCell>新カテゴリ</TableCell>
                  <TableCell>旧カテゴリ</TableCell>
                  <TableCell>申請対象文書</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {search.errorSentences.map((c, i) => {
                  return (
                    <TableRow key={i}>
                      <TableCell>{c.name}</TableCell>
                      <TableCell>{statusPatterns[c.status]}</TableCell>
                      <TableCell>{c.fileName}</TableCell>
                      <TableCell>
                        { c.status === 10 ? (
                        <Select
                          id="set-new-category"
                          name="new-category"
                          value={newCategories[i] ? newCategories[i] : ''}
                          onChange={this.handleChangeNewTagId}
                        >
                          <MenuItem value="">
                            <em></em>
                          </MenuItem>
                          {tags ? Object.keys(tags.tags).map(k => (
                            <MenuItem key={k} value={k} data-category_key={i}>
                              {tags.tags[k]}
                            </MenuItem>
                          )) : (null)}
                        </Select>
                        ) : (tags && tags.tags[c.newTagId])
                        }
                      </TableCell>
                      <TableCell>{tags && tags.tags[c.orgTagId]}</TableCell>
                      <TableCell>{c.sentence.substr(0, 20)}...</TableCell>
                      <TableCell>
                        <Button
                          className={classes.actionButton}
                          variant="contained"
                          color="default"
                          onClick={this.openModal}
                          data-sentence={c.sentence}
                        >
                          文書表示
                        </Button>
                        <Button
                          className={classes.actionButton}
                          variant="contained"
                          color="primary"
                          onClick={this.handleUpdate}
                          data-index={i}
                          data-id={c.id}
                          data-new_status={
                            (c.status === 10) ? (20) :
                            (c.status === 20) ? (10) : (null)
                          }
                        >
                          {(c.status === 10) ? ('確定') :
                            (c.status === 20) ? ('確定取消') : (null)}
                        </Button>
                        {c.status !== 20 ? (
                          <Button
                            className={classes.actionButton}
                            variant="contained"
                            color="secondary"
                            onClick={this.handleDelete}
                            data-index={i}
                            data-id={c.id}
                          >
                            削除
                          </Button>
                        ) : (null)
                        }
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            component="div"
            count={search.totalCount}
            page={conditions.page}
            rowsPerPage={conditions.limit}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeRowsPerPage}
          />
        </Paper>
        <Dialog
        open={ this.state.modalIsOpen }
        onClose={ this.closeModal }
        fullWidth={ true }
        maxWidth="md"
        aria-labelledby="apply-dialog-title"
        >
          <DialogContent>
            <DialogContentText>
              { this.state.targetSentence }
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeModal} color="primary">
              閉じる
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }
}

export default withStyles(style)(SearchList);



// WEBPACK FOOTER //
// ./src/components/SearchList.tsx